/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CustomAppScriptTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import Editor from '@monaco-editor/react';
import { FileCode, RotateCcw } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { getDefaultScript } from '../utils/customAppDefaults';

interface CustomAppScriptTabProps {
    appType: 'lua' | 'xml';
    scriptContent: string;
    onScriptContentChange: (v: string) => void;
    t: (key: string, fallback?: string) => string;
}

export function CustomAppScriptTab({ appType, scriptContent, onScriptContentChange, t }: CustomAppScriptTabProps) {
    return (
        <FormBox title={t('customApplications.sections.script')} icon={<FileCode className="h-5 w-5" />}>
            <div className="space-y-3">
                {/* Header: language indicator + reset */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase">
                            {appType === 'lua' ? 'Lua Script' : 'XML Dialplan'}
                        </span>
                        <span className="text-xs text-muted-foreground">�</span>
                        <span className="text-xs text-muted-foreground">
                            {t('customApplications.form.scriptContentHelp')}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => onScriptContentChange(getDefaultScript(appType))}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        title={t('common.reset', 'Reset to default')}
                    >
                        <RotateCcw className="h-3 w-3" />
                        {t('common.reset', 'Reset')}
                    </button>
                </div>

                {/* Monaco Editor */}
                <div className="border border-border rounded-lg overflow-hidden">
                    <Editor
                        height="500px"
                        language={appType === 'lua' ? 'lua' : 'xml'}
                        value={scriptContent}
                        onChange={(value) => onScriptContentChange(value || '')}
                        theme="vs-dark"
                        options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            lineNumbers: 'on',
                            scrollBeyondLastLine: false,
                            wordWrap: 'on',
                            tabSize: 2,
                            automaticLayout: true,
                            padding: { top: 12, bottom: 12 },
                            renderLineHighlight: 'all',
                            cursorBlinking: 'smooth',
                            smoothScrolling: true,
                            bracketPairColorization: { enabled: true },
                            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace"
                        }}
                    />
                </div>
            </div>
        </FormBox>
    );
}
